import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FaCaretDown, FaCaretUp } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { BorderSizeEnum, BorderTypeEnum, ColorsEnum, ColorsToneEnum, SpacingEnum } from 'types/cssTypes'
import { SlotData } from 'utils/dataExtractor'
import { EntityView } from './EntityView'

/** ===== Constants ===== */
const TOGGLE_ANIMATION_MS = 240
const RESIZE_DEBOUNCE_MS = 150
const TOGGLE_BUTTON_PADDING_RIGHT = 70

interface ClaimSnapshotProps {
  data: SlotData[]
  billedAmount?: SlotData | SlotData[]
  includeSlots?: string[]
}

export const ClaimSnapshot = ({ data, billedAmount, includeSlots }: ClaimSnapshotProps) => {
  /** UI state */
  const [isExpanded, setIsExpanded] = useState(false)
  const [firstRowHeightPx, setFirstRowHeightPx] = useState<number | null>(null)
  const [firstRowIndices, setFirstRowIndices] = useState<number[]>([])
  const [hasOverflowRows, setHasOverflowRows] = useState(false)
  const [isMeasuring, setIsMeasuring] = useState(true)

  /** Refs (imperative bookkeeping) */
  const isMeasuringRef = useRef(false)
  const resizeDebounceRef = useRef<NodeJS.Timeout | null>(null)
  const lastMeasuredWidthRef = useRef<number>(0)

  /** Derived inputs */
  const billedSlot = Array.isArray(billedAmount) ? billedAmount[0] : billedAmount

  const orderedSlots = useMemo(() => {
    if (!includeSlots?.length) return data
    const wanted = includeSlots.map((s) => s.trim()).filter(Boolean)
    const bySlot = new Map(data.map((d) => [d.slot, d] as const))
    const result: SlotData[] = []
    const seen = new Set<string>()

    for (const slot of wanted) {
      const item = bySlot.get(slot)
      if (item) {
        result.push(item)
        seen.add(item.slot)
      }
    }
    const remainder = data.filter((d) => !seen.has(d.slot))
    return [...result, ...remainder]
  }, [data, includeSlots])

  /** DOM refs for measurement */
  const gridRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  /** Measure which items belong to the first row and its height */
  const measureFirstRowLayout = useCallback(() => {
    const container = gridRef.current
    if (!container || orderedSlots.length === 0) return

    const nodes = itemRefs.current
      .map((n, i) => ({ n, i }))
      .filter((x): x is { n: HTMLDivElement; i: number } => Boolean(x.n))

    if (nodes.length === 0) return

    const firstTop = Math.min(...nodes.map(({ n }) => n.offsetTop))
    const row1 = nodes.filter(({ n }) => n.offsetTop === firstTop)
    const row1Idx = row1.map(({ i }) => i)
    const maxBottom = Math.max(...row1.map(({ n }) => n.offsetTop + n.offsetHeight))
    const rowHeight = Math.max(0, maxBottom - firstTop)
    const moreRowsExist = nodes.some(({ n }) => n.offsetTop > firstTop)

    setFirstRowIndices(row1Idx)
    setFirstRowHeightPx(rowHeight)
    setHasOverflowRows(moreRowsExist)
  }, [orderedSlots])

  /** Initial measurement */
  useLayoutEffect(() => {
    isMeasuringRef.current = true
    setIsMeasuring(true)
    requestAnimationFrame(() => {
      measureFirstRowLayout()
      requestAnimationFrame(() => {
        isMeasuringRef.current = false
        setIsMeasuring(false)
      })
    })
  }, [measureFirstRowLayout])

  /** Re-measure on container resize (width changes only), debounced */
  useLayoutEffect(() => {
    const container = gridRef.current
    if (!container) return

    lastMeasuredWidthRef.current = container.getBoundingClientRect().width

    const ro = new ResizeObserver((entries) => {
      if (isMeasuringRef.current) return

      const newWidth = entries[0]?.contentRect.width
      // Ignore height-only changes (which can come from our own expand/collapse)
      if (newWidth && Math.abs(newWidth - lastMeasuredWidthRef.current) < 1) return
      lastMeasuredWidthRef.current = newWidth || lastMeasuredWidthRef.current

      if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current)

      resizeDebounceRef.current = setTimeout(() => {
        if (isMeasuringRef.current) return

        isMeasuringRef.current = true
        setIsMeasuring(true)
        requestAnimationFrame(() => {
          measureFirstRowLayout()
          requestAnimationFrame(() => {
            isMeasuringRef.current = false
            setIsMeasuring(false)
          })
        })
      }, RESIZE_DEBOUNCE_MS)
    })

    ro.observe(container)
    return () => {
      ro.disconnect()
      if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current)
    }
  }, [measureFirstRowLayout])

  const shouldShowToggle = hasOverflowRows

  /** Smoothly expand/collapse the grid without layout “jerk” */
  const handleToggleExpanded = () => {
    const el = gridRef.current
    if (!el) {
      setIsExpanded((s) => !s)
      return
    }

    const from = el.getBoundingClientRect().height
    setIsExpanded((s) => !s)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const to = el.getBoundingClientRect().height
        el.style.height = `${from}px`
        el.style.overflow = 'hidden'
        el.style.transition = `height ${TOGGLE_ANIMATION_MS}ms ease`
        // Force style flush
        void el.offsetHeight
        el.style.height = `${to}px`

        const cleanup = () => {
          el.style.height = ''
          el.style.overflow = ''
          el.style.transition = ''
          el.removeEventListener('transitionend', cleanup)
        }
        el.addEventListener('transitionend', cleanup)
      })
    })
  }

  return (
    <Root>
      <div className="left-wrapper">
        <div className="bill-amount">
          <div className="highlight-strong ba-title">Claim Billed Amount</div>
          <div className="highlight-strong ba-value">{formatCurrency(billedSlot?.value)}</div>
        </div>
      </div>

      <div className="right-wrapper">
        <div
          ref={gridRef}
          className="inner-right-wrapper"
          style={{
            maxHeight: !isExpanded && firstRowHeightPx ? `${firstRowHeightPx}px` : undefined,
            overflow: !isExpanded ? 'hidden' : undefined,
            paddingRight: shouldShowToggle ? TOGGLE_BUTTON_PADDING_RIGHT : 0,
          }}
          aria-expanded={isExpanded}
        >
          {orderedSlots.map((slot, idx) => {
            const isInFirstRow = firstRowIndices.includes(idx)
            const shouldRender = isExpanded || isMeasuring || isInFirstRow

            return (
              <div
                key={`${slot.slot}-${slot.entId}-${idx}`}
                ref={(el) => {
                  itemRefs.current[idx] = el
                }}
                className="label-set-container"
                style={{ display: shouldRender ? undefined : 'none' }}
              >
                <EntityView {...slot} />
              </div>
            )
          })}
        </div>

        {shouldShowToggle && !isMeasuring && (
          <div className="show-more-container">
            <Button onClick={handleToggleExpanded} aria-expanded={isExpanded}>
              {isExpanded ? 'Less' : 'More'} {isExpanded ? <FaCaretUp /> : <FaCaretDown />}
            </Button>
          </div>
        )}
      </div>
    </Root>
  )
}

/** Formats currency safely for strings or numbers */
export function formatCurrency(v?: string | number) {
  const n = Number(v ?? 0)
  if (Number.isNaN(n)) return (v as string) ?? '—'
  return `$ ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/** ===== Styles ===== */
const Root = styled.div`
  display: flex;

  .label-set-container {
    margin-right: ${({ theme }) => theme.spacing[SpacingEnum.xs]};
    padding: ${({ theme }) => theme.spacing[SpacingEnum.xxs]} 0;
    white-space: nowrap;
    min-width: 0;
    line-height: 1.25;
  }

  .left-wrapper {
    margin: 0.125rem 1.75rem 0.25rem -0.01rem;
  }

  .right-wrapper {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
  }

  .inner-right-wrapper {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    flex: 1;
    transition: height ${TOGGLE_ANIMATION_MS}ms ease;
  }

  .bill-amount {
    width: 11.25rem;
    height: 100%;
    flex-grow: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 2.25rem 1.5rem 1.75rem;
    border-radius: 8px;
    border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
    background-color: ${({ theme }) => theme.colors.neutral.lighter};
  }

  .ba-title {
    ${({ theme }) => theme.typography.xs.medium};
    color: ${({ theme }) => theme.colors.grey.neutral};
  }
  .ba-value {
    ${({ theme }) => theme.typography.m.medium} !important;
    color: ${({ theme }) => theme.colors.grey.dark} !important;
    font-size: 20px !important;
  }

  .show-more-container {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  .show-more-container button {
    width: 58px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 4px 4px 4px 8px;
    border-radius: 4px;
    border: ${({ theme }) =>
      theme.borders[BorderSizeEnum.s][BorderTypeEnum.solid](ColorsEnum.grey, ColorsToneEnum.lighter)};
    background-color: ${({ theme }) => theme.colors[ColorsEnum.grey][ColorsToneEnum.lightest]};
    font-family: Milliard;
    font-size: 12px;
    font-weight: bold;
    color: ${({ theme }) => theme.colors[ColorsEnum.grey][ColorsToneEnum.neutral]};
  }
`
